import { lazy, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { faBolt, faRectangleTerminal } from '@fortawesome/pro-thin-svg-icons';

import { DedicatedAppName, Job, ResponseError } from '@typings';
import { getJob } from '@services';
import { as, invariant, path } from '@utils';

import { ChaseSpinner } from '@components';
import { JobLayout, Layout } from '@components/Layouts';
import { EmptyContent } from '@components/Ui';

import { withSuspense } from './utils';

const APP_ID_DELIMITER = 'app-installer:';
const APP_TYPE_DELIMITER = /(target:|platform-app-type:)/;

const JobOverviewPage = lazy(() => import('@pages/Job/JobOverviewPage'));
const JobLogPage = lazy(() => import('@pages/Job/JobLogPage'));
const JobObservabilityPage = lazy(
  () => import('@pages/Job/JobObservabilityPage'),
);
const JobOutputsPage = lazy(() => import('@pages/Job/JobOutputsPage'));

type Props = {
  isJobPage: boolean;
};

export default function JobRoutes({ isJobPage }: Props) {
  const params = useParams();
  const jobId = params.jobId!;

  const [job, setJob] = useState<Job.Model | null>(null);
  const [isAppInstalled, setAppInstalation] = useState(false);
  const [error, setError] = useState<'not-found' | 'permission'>();

  const { id, name, tags = [] } = as.o<Job.Model>(job);
  const title = name ?? id ?? jobId;
  const type = isJobPage ? 'job' : 'app';
  const icon = isJobPage ? faBolt : faRectangleTerminal;

  const appId = tags
    .find((tag) => tag.includes(APP_ID_DELIMITER))
    ?.split(APP_ID_DELIMITER)[1];
  const appType = tags
    .find((tag) => APP_TYPE_DELIMITER.test(tag))
    ?.split(APP_TYPE_DELIMITER)[2] as DedicatedAppName | undefined;

  const fetchJob = useCallback(async () => {
    try {
      invariant(jobId);

      const job = await getJob({ jobId });

      setJob(job);
    } catch (error) {
      const { error: errorMessage } = error as ResponseError;

      const isJobNotFound = !!errorMessage;

      setError(isJobNotFound ? 'not-found' : 'permission');
    }
  }, [jobId]);

  const fetchApp = useCallback(async () => {
    try {
      if (!appId || isAppInstalled) {
        return;
      }

      await getJob({ jobId: appId });

      setAppInstalation(true);
    } catch (error) {
      const { error: errorMessage } = error as ResponseError;

      const isJobNotFound = !!errorMessage;

      setError(isJobNotFound ? 'not-found' : 'permission');
    }
  }, [appId, isAppInstalled]);

  useEffect(() => {
    const INTERVAL_TIMEOUT = 10_000;

    fetchJob();

    const intervalId = setInterval(fetchJob, INTERVAL_TIMEOUT);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchJob]);

  useEffect(() => {
    const INTERVAL_TIMEOUT = 5_000;

    fetchApp();

    const intervalId = setInterval(fetchApp, INTERVAL_TIMEOUT);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchApp]);

  const getdefaultPath = () => {
    return path.job(id, Job.Tab.Overview);
  };

  const renderContent = () => {
    if (error === 'not-found') {
      return (
        <EmptyContent
          icon={icon}
          title={`No such ${type}`}
          text={`The ${type} you are searching for does not exist or may have been removed`}
        />
      );
    }

    if (error === 'permission') {
      return (
        <EmptyContent
          icon={icon}
          title="Permission Required"
          text={`You can't view this ${type}. Please contact your administrator to request access to the project or organization`}
        />
      );
    }

    return <ChaseSpinner color="black" className="absolute mt-40 h-14 w-14" />;
  };

  if (!job) {
    return (
      <Layout backNavigation title={title}>
        <Layout.Content className="relative flex justify-center">
          {renderContent()}
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Routes>
      <Route element={<JobLayout job={job} getJob={fetchJob} />}>
        <Route index element={withSuspense(JobOverviewPage, { job })} />
        <Route
          path="details"
          element={withSuspense(JobOverviewPage, { job })}
        />
        <Route path="log" element={withSuspense(JobLogPage)} />
        <Route
          path="observability"
          element={withSuspense(JobObservabilityPage, { job })}
        />
        <Route
          path="outputs"
          element={withSuspense(JobOutputsPage, { appName: name, appType })}
        />
        <Route path="*" element={<Navigate replace to={getdefaultPath()} />} />
      </Route>
    </Routes>
  );
}
